/**
 * The ONE backoff shape for client-side poll loops (#754).
 *
 * Healthy (zero consecutive failures): the plain base cadence, unjittered — a
 * working poller should feel exactly as responsive as its interval promises.
 *
 * Failing: exponential stretch (capped exponent, hard ceiling) with
 * half-to-full jitter. The jitter matters for the same reason it does in
 * `withConnectionRetry`: every open tab starts failing on the same burst, and
 * a fixed multiplier would march them all back onto the recovering database
 * in step.
 *
 * History: the notification bell had this shape (#711) and never overlapped a
 * request; the three pollers written WITHOUT it (image status, generating
 * page, translation watch) are the ones that stacked requests during database
 * slowness and drove the EMAXCONN spiral (#754). Extracted so the correct
 * shape is the default for the next poller, not something to rediscover.
 * Pair it with a chained setTimeout — never setInterval, which fires on
 * schedule whether or not the previous tick resolved.
 */
export function pollBackoffDelay(
  baseMs: number,
  consecutiveFailures: number,
  ceilingMs = 120_000,
): number {
  if (consecutiveFailures === 0) return baseMs;
  const stretched = Math.min(baseMs * 2 ** Math.min(consecutiveFailures, 5), ceilingMs);
  return stretched / 2 + Math.random() * (stretched / 2);
}
